import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Performance from "./Performance/Performance";
import BudgetControl from "./BudgetControl/BudgetControl";
import Overview from "./Overview/Overview";
import Anomalies from "./Anomalies/Anomalies";
import BeAware from "./BeAware/BeAware";
import {
  clearOverviewConversionsFilterOptions,
  clearOverviewConversionsFilters,
  setIsOverviewConversionsFilterLoading,
  setIsOverviewConversionsLoading,
  setIsOverviewConversionsNotesLoading,
  setOverviewConversionsBaseFilterOptionsLoaded,
  setOverviewConversionsDataSet,
  setOverviewConversionsLoadedRequestKey,
  setOverviewConversionsNotes,
  setOverviewConversionsNotesLoadedRequestKey,
  setOverviewConversionsOutput,
} from "../../../../features/bizDevelopmentDashboardSlice";
import { useBizDevelopmentDashboard } from "../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboard";

const TAB_KEYS = [
  "performance",
  "budget_control",
  "overview",
  "anomalies",
  "be_aware",
];

const isAnomaliesEnabled = import.meta.env.VITE_ENV === "development"; // env-check: intentional

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const BizDevelopmentDashboardTabs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const tabFromUrl = searchParams.get("tab");
  const initialTab = tabFromUrl ? Math.max(TAB_KEYS.indexOf(tabFromUrl), 0) : 0;
  const [value, setValue] = React.useState(initialTab);

  const { fetchBudgetData } = useBizDevelopmentDashboard();

  const isFetchedBudgetData = useSelector(
    (state: any) => state.bizDevelopmentDashboard.isFetchedBudgetData,
  );

  const resetOverviewState = () => {
    //Overview Dashboard
    dispatch(setIsOverviewConversionsLoading(false));
    dispatch(setOverviewConversionsOutput([]));
    dispatch(setOverviewConversionsLoadedRequestKey(null));
    dispatch(setOverviewConversionsDataSet("action"));
    dispatch(clearOverviewConversionsFilters());
    dispatch(clearOverviewConversionsFilterOptions());
    dispatch(setIsOverviewConversionsFilterLoading(false));
    dispatch(setIsOverviewConversionsNotesLoading(false));
    dispatch(setOverviewConversionsNotes([]));
    dispatch(setOverviewConversionsNotesLoadedRequestKey(null));
    dispatch(setOverviewConversionsBaseFilterOptionsLoaded(false));
  };

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    if (TAB_KEYS[value] === "overview" && TAB_KEYS[newValue] !== "overview") {
      resetOverviewState();
    }

    //Fetch budget data only on first switch to budget_control tab
    if (TAB_KEYS[newValue] === "budget_control") {
      if (!isFetchedBudgetData) {
        fetchBudgetData();
      }
    }
    setValue(newValue);
    setSearchParams((prev) => {
      const sites = prev.get("sites");
      const next = new URLSearchParams();
      if (sites) next.set("sites", sites);
      if (TAB_KEYS[newValue] === "performance") {
        const timeframe = prev.get("timeframePerformance");
        if (timeframe) next.set("timeframePerformance", timeframe);
      }
      if (TAB_KEYS[newValue] === "overview") {
        const overviewAccountSummaryTimeframe = prev.get(
          "timeframeOverviewAccountSummary",
        );
        const pageOverviewAccountSummary = prev.get(
          "pageOverviewAccountSummary",
        );
        const rowsPerPageOverviewAccountSummary = prev.get(
          "rowsPerPageOverviewAccountSummary",
        );
        const overviewTab = prev.get("overviewTab");
        if (overviewTab) {
          next.set("overviewTab", overviewTab);
        }
        if (overviewAccountSummaryTimeframe) {
          next.set(
            "timeframeOverviewAccountSummary",
            overviewAccountSummaryTimeframe,
          );
        }
        if (pageOverviewAccountSummary) {
          next.set("pageOverviewAccountSummary", pageOverviewAccountSummary);
        }
        if (rowsPerPageOverviewAccountSummary) {
          next.set(
            "rowsPerPageOverviewAccountSummary",
            rowsPerPageOverviewAccountSummary,
          );
        }
      }
      const timeframe = prev.get("timeframePerformance");
      const additionalTimeframe = prev.get("additionalTimeframePerformance");
      if (sites) next.set("sites", sites);
      if (timeframe) next.set("timeframePerformance", timeframe);
      if (additionalTimeframe)
        next.set("additionalTimeframePerformance", additionalTimeframe);
      next.set("tab", TAB_KEYS[newValue]);
      return next;
    });
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="biz development dashboard tabs"
            sx={{
              "& .MuiTab-root": {
                color: "#00000099",
                "&.Mui-selected": { color: "#9C27B0" },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#9C27B0",
                height: "3px",
                borderRadius: "10px",
              },
              "& .MuiTabs-flexContainer": { justifyContent: "flex-start" },
              "& .MuiTab-root:first-of-type": { paddingLeft: 1.5 },
            }}
          >
            <Tab label="Performance" {...a11yProps(0)} />
            <Tab label="Budget Control" {...a11yProps(1)} />
            <Tab label="Overview" {...a11yProps(2)} />
            <Tab
              label="Anomalies"
              {...a11yProps(3)}
              disabled={!isAnomaliesEnabled}
            />
            <Tab label="Be Aware" data-tracker="852" {...a11yProps(4)} />
          </Tabs>
        </Box>
        <CustomTabPanel value={value} index={0}>
          <Performance />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <BudgetControl />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          <Overview />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={3}>
          <Anomalies />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={4}>
          <BeAware />
        </CustomTabPanel>
      </Box>
    </>
  );
};

export default BizDevelopmentDashboardTabs;
